import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { POST_LOGIN_REDIRECT_KEY } from './LoginPage';
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, ShieldX, Loader2, CheckCircle2, XCircle, AlertTriangle,
  LogIn, Lock
} from "lucide-react";
import { clientDisplayName } from "../utils/clientDisplayName";

// ==================================================================
// BM-H267 (03 Sep 2026): the approval link is no longer a credential.
//
// The backend now requires an authenticated session belonging to the
// director the token was issued to, and returns a structured detail
// ``{ error, message }`` on refusal. The page has to say which of the
// two things went wrong, because they need different actions from the
// reader: sign in, or stop and ask for the request to be resent.
//
// The detail is read defensively: older handlers on this router still
// return a plain string detail (404 "Approval not found"), so anything
// that is not our structured shape falls through to the generic panel.
// ==================================================================
const detailMessage = (detail, fallback) => {
  if (detail && typeof detail === "object") return detail.message || fallback;
  if (typeof detail === "string" && detail) return detail;
  return fallback;
};

const authProblemFrom = (e) => {
  const status = e?.response?.status;
  if (status !== 401 && status !== 403) return null;
  const detail = e?.response?.data?.detail;
  const code = (detail && typeof detail === "object" && detail.error) || "";
  return {
    kind: status === 401 ? "signin" : "forbidden",
    code,
    message: detailMessage(
      detail,
      status === 401
        ? "You need to be signed in to CaseMinder as the director this approval was sent to."
        : "You are not authorised to action this approval.",
    ),
  };
};

// BM-H671 (Vanessa, 29 Sep 2026): the Clearance Approved screen was a dead
// end. The next step is sending the client onboarding, from the matter page
// (or the pipeline row if the reference is still being set up).
function ApprovalOnwardLinks({ matterRef }) {
  return (
    <div className="mt-5 flex flex-wrap justify-center gap-3" data-testid="approval-onward-links">
      {matterRef && (
        <Link to={`/matter/${encodeURIComponent(matterRef)}`} data-testid="approval-open-matter">
          <Button className="bg-teal hover:bg-teal/90 text-white">Open matter to send onboarding</Button>
        </Link>
      )}
      <Link to="/pipeline" data-testid="approval-open-pipeline">
        <Button variant="outline">Go to pipeline</Button>
      </Link>
    </div>
  );
}

export default function ConflictApprovalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [result, setResult] = useState(null);
  const [authProblem, setAuthProblem] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/conflict-approval/review/${token}`);
        setData(res.data);
        // Check if already decided
        if (res.data.conflict_status !== "pending_approval") {
          setResult({
            status: res.data.approval_decision || (res.data.conflict_status === "clear_with_conditions" ? "approved" : "rejected"),
            message: res.data.conflict_status === "clear_with_conditions" 
              ? "This clearance has already been approved" 
              : "This clearance has already been rejected",
            provisional_id: res.data.provisional_id,
            matter_reference: res.data.matter_reference || res.data.provisional_id,
          });
        }
      } catch (e) {
        console.error("Approval page error:", e.response?.status, e.response?.data);
        const detail = e.response?.data?.detail;
        const problem = authProblemFrom(e);
        if (problem) {
          setAuthProblem(problem);
        } else if (e.response?.status === 404) {
          setError(detail || "Approval not found — it may have already been processed or the link has expired.");
        } else {
          setError(detail || `Failed to load approval (error ${e.response?.status || "network"}).`);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // ================================================================
  // BM-H139g (01 Sep 2026): 202-accept phased decision flow.
  //
  // Old handler awaited a synchronous POST that ran 30-180 s of
  // SharePoint work inside the Azure request cycle and hit the ~120 s
  // proxy idle timeout on cold Graph tokens.  The old FE had no
  // timeout, no retry, and a generic error toast — the exact symptom
  // Vanessa saw ("Failed to submit decision" after a long spin).
  //
  // New flow:
  //   1. POST /decide with a 90 s hard axios timeout.  Backend now
  //      returns in <1 s with provisioning_status ∈ {in_progress,
  //      completed, n/a}.
  //   2. If completed / n/a / rejection: show final panel immediately.
  //   3. If in_progress: enter a "provisioning" panel and poll
  //      /status-by-token/{token} every 2 s (up to 90 attempts / 3 min)
  //      until provisioning_status flips.
  //   4. If failed: show explicit failure panel telling the supervisor
  //      the DECISION was recorded but matter provisioning needs admin.
  // ================================================================
  const POLL_INTERVAL_MS = 2000;
  const POLL_MAX_ATTEMPTS = 90;  // 90 * 2 s = 3 min

  const pollForProvisioning = async () => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const res = await axios.get(
          `${API}/conflict-approval/status-by-token/${token}`,
          { timeout: 15000 }
        );
        const s = res.data.provisioning_status;
        if (s && s !== "in_progress") {
          return res.data;
        }
      } catch (e) {
        // Transient network / 5xx — keep polling silently; we only
        // surface a failure after the whole attempt budget is exhausted.
        console.warn("provisioning poll error", attempt, e?.response?.status);
      }
    }
    return { provisioning_status: "timeout" };
  };

  // BM-H4514: an approval says whether it is no conflict ("clear") or
  // cleared with conditions; it used to record conditions every time.
  const handleDecision = async (approved, outcome = null) => {
    if (outcome === "clear_with_conditions" && reason.trim().length < 10) {
      toast.error("State the conditions in Reason / Notes before approving with conditions.");
      return;
    }
    setDeciding(true);
    try {
      const res = await axios.post(
        `${API}/conflict-approval/review/${token}/decide`,
        { approved, supervisor_reason: reason, outcome },
        { timeout: 90000 }
      );
      const first = res.data;

      // Rejection / already-complete path — done in one round-trip.
      if (first.provisioning_status !== "in_progress") {
        setResult(first);
        toast.success(first.message);
        return;
      }

      // Approved + provisioning in progress — show interim panel and poll.
      setResult({ ...first, _polling: true });
      const final = await pollForProvisioning();

      const merged = {
        ...first,
        ...final,
        _polling: false,
      };

      if (final.provisioning_status === "completed") {
        merged.message = final.matter_reference
          ? `Conflict clearance approved — matter reference: ${final.matter_reference}`
          : "Conflict clearance approved.";
        toast.success(merged.message);
      } else if (final.provisioning_status === "failed") {
        merged.message =
          "Your approval was recorded, but the system could not finish setting "
          + "up the matter reference. An administrator has been notified and will "
          + "complete provisioning shortly. No further action is needed from you.";
        toast.error("Matter provisioning needs admin — decision was still recorded.");
      } else if (final.provisioning_status === "timeout") {
        merged.message =
          "Your approval was recorded. Matter reference generation is taking longer "
          + "than usual; an administrator will be notified automatically. You may "
          + "close this page.";
        toast.info("Approval recorded — provisioning still running in background.");
      }
      setResult(merged);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const problem = authProblemFrom(e);
      if (problem) {
        // BM-H267: the decision was refused on authorisation, so say so on
        // the page rather than in a toast that scrolls away. Nothing was
        // recorded.
        setAuthProblem(problem);
        toast.error(problem.message);
      } else if (e.code === "ECONNABORTED") {
        toast.error("The server took too long to respond. Your click may not have been recorded — please refresh and try again.");
      } else if (e.response?.status === 409) {
        // BM-H267: single-use token already spent.
        toast.error(detailMessage(detail, "This approval link has already been used."));
        setAuthProblem({
          kind: "forbidden",
          code: "conflict_approval_token_already_used",
          message: detailMessage(detail, "This approval link has already been used."),
        });
      } else {
        toast.error(detailMessage(detail, `Failed to submit decision (${e.response?.status || "network"}).`));
      }
    } finally {
      setDeciding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal" />
      </div>
    );
  }

  // BM-H267: authorisation panels. Deliberately ahead of the generic error
  // panel, and deliberately not a redirect to /login: the reader arrived from
  // an email and needs to be told plainly what happened, and a director who
  // was not the intended recipient must not be nudged into trying again.
  if (authProblem) {
    const needsSignIn = authProblem.kind === "signin";
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-4">
        <div className="bg-navy border border-navy-border rounded-lg p-8 max-w-md w-full text-center">
          {needsSignIn ? (
            <LogIn className="w-12 h-12 text-teal mx-auto mb-4" />
          ) : (
            <Lock className="w-12 h-12 text-status-amber mx-auto mb-4" />
          )}
          <h1 className="text-xl font-bold text-white mb-2">
            {needsSignIn ? "Sign In Required" : "Not Authorised"}
          </h1>
          <p className="text-txt-muted text-sm mb-4">{authProblem.message}</p>
          {needsSignIn ? (
            <>
              <Button
                onClick={() => {
                  try {
                    window.sessionStorage.setItem(
                      POST_LOGIN_REDIRECT_KEY,
                      window.location.pathname + window.location.search,
                    );
                  } catch {
                    // Storage unavailable; sign-in still works, the director
                    // just lands on the dashboard and reopens the link.
                  }
                  window.location.assign("/login");
                }}
                className="bg-teal text-navy hover:bg-teal/90"
              >
                Sign in to CaseMinder
              </Button>
              <p className="text-txt-muted text-xs mt-4">
                Signing in returns you to this approval.
              </p>
            </>
          ) : (
            <p className="text-txt-muted text-xs">
              This attempt has been recorded in the matter audit log. Nothing
              about the conflict check has been changed.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-4">
        <div className="bg-navy border border-navy-border rounded-lg p-8 max-w-md w-full text-center">
          <ShieldX className="w-12 h-12 text-status-red mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Approval Not Found</h1>
          <p className="text-txt-muted text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (result) {
    const isProvisioning = result._polling === true;
    const provisioningFailed = result.provisioning_status === "failed";
    const provisioningTimeout = result.provisioning_status === "timeout";

    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-4">
        <div className="bg-navy border border-navy-border rounded-lg p-8 max-w-md w-full text-center">
          {isProvisioning ? (
            <>
              <Loader2 className="w-12 h-12 text-teal mx-auto mb-4 animate-spin" />
              <h1 className="text-xl font-bold text-white mb-2">Provisioning Matter Reference…</h1>
              <p className="text-txt-muted text-sm mb-2">
                Your approval was recorded. Setting up the matter now — this usually takes 10-30 seconds.
              </p>
              <p className="text-txt-muted text-xs">You can safely close this page; it will finish in the background.</p>
            </>
          ) : provisioningFailed ? (
            <>
              <AlertTriangle className="w-12 h-12 text-status-amber mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-2">Approval Recorded — Admin Follow-up Needed</h1>
              <p className="text-txt-muted text-sm">{result.message}</p>
            </>
          ) : provisioningTimeout ? (
            <>
              <CheckCircle2 className="w-12 h-12 text-teal mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-2">Approval Recorded</h1>
              <p className="text-txt-muted text-sm">{result.message}</p>
              <ApprovalOnwardLinks matterRef={null} />
            </>
          ) : result.status === "approved" ? (
            <>
              <CheckCircle2 className="w-12 h-12 text-teal mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-2">Clearance Approved</h1>
              <p className="text-txt-muted text-sm mb-2">{result.message}</p>
              {(result.matter_reference || result.provisional_id) && (
                <p className="text-teal font-mono text-sm">Matter Reference: {result.matter_reference || result.provisional_id}</p>
              )}
              <ApprovalOnwardLinks matterRef={result.matter_reference || result.provisional_id} />
            </>
          ) : (
            <>
              <XCircle className="w-12 h-12 text-status-red mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-2">Clearance Rejected</h1>
              <p className="text-txt-muted text-sm">{result.message}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Build match details from hits_summary
  const hits = data.hits_summary || {};
  const matchEntries = [];
  if (typeof hits === "object" && hits.results) {
    for (const match of hits.results) {
      matchEntries.push(match);
    }
  }

  return (
    <div className="min-h-screen bg-navy py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-status-amber-dim/30 border border-amber-500/50 rounded-lg p-6 text-center">
          <ShieldAlert className="w-10 h-10 text-status-amber mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white mb-1">Conflict Clearance — Approval Required</h1>
          <p className="text-status-amber/80 text-sm">
            <strong>{data.decision_maker_name}</strong> has requested conditional clearance for a potential conflict.
          </p>
        </div>

        {/* Conflict details */}
        <div className="bg-navy border border-navy-border rounded-lg overflow-hidden">
          <div className="p-5 border-b border-navy-border">
            <h2 className="text-base font-semibold text-white">Conflict Check Details</h2>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <Detail label="Client Name" value={clientDisplayName(data)} />
            <Detail label="Fee Earner" value={data.decision_maker_name} />
            <Detail label="Search Query" value={data.search_query} />
            <Detail label="Submitted" value={data.created_at ? new Date(data.created_at).toLocaleDateString("en-GB") : "-"} />
          </div>
          {data.decision_notes && (
            <div className="mx-5 mb-5 bg-navy-hover border-l-3 border-navy-border p-3 rounded">
              <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-1">Fee Earner Notes</p>
              <p className="text-sm text-txt-muted">{data.decision_notes}</p>
            </div>
          )}
        </div>

        {/* Match details */}
        <div className="bg-navy border-2 border-amber-500/50 rounded-lg overflow-hidden">
          <div className="p-4 bg-status-amber-dim/20 border-b border-amber-500/40">
            <h2 className="text-base font-semibold text-status-amber flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Potential Matches Found
            </h2>
          </div>
          <div className="p-4 space-y-3">
            {matchEntries.length > 0 ? matchEntries.map((match, i) => (
              <div key={i} className="p-3 bg-status-amber-dim/30 border border-amber-500/30 rounded">
                <p className="font-semibold text-sm text-white">{match.entity_name || match.name || match.client_name || "Unknown"}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-txt-muted">
                  {match.matter_reference && <span>Ref: <span className="font-mono">{match.matter_reference}</span></span>}
                  {match.matter_description && <span>{match.matter_description}</span>}
                  {match.match_type && <span className="text-status-amber">Match: {match.match_type}</span>}
                  {match.entity_type && <span>Type: {match.entity_type}</span>}
                </div>
              </div>
            )) : (
              <p className="text-sm text-txt-muted">Match details are available in the original conflict check record.</p>
            )}
          </div>
        </div>

        {/* Decision form */}
        <div className="bg-navy border border-navy-border rounded-lg p-5 space-y-4">
          <h2 className="text-base font-semibold text-white">Your Decision</h2>
          <div>
            <label className="text-sm text-txt-muted block mb-1">Reason / Notes</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Record your reason for approving or rejecting this clearance..."
              className="bg-navy-hover border-navy-border text-white placeholder-[#7a86a8]"
              rows={3}
              data-testid="approval-reason"
            />
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => handleDecision(true, "clear")}
              disabled={deciding}
              className="flex-1 bg-teal hover:bg-teal text-white"
              data-testid="approve-btn"
            >
              {deciding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Approve: no conflict
            </Button>
            <Button
              onClick={() => handleDecision(true, "clear_with_conditions")}
              disabled={deciding}
              variant="outline"
              className="flex-1 border-amber-500/50 text-status-amber hover:bg-status-amber-dim/20"
              data-testid="approve-conditions-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Approve with conditions
            </Button>
            <Button
              onClick={() => handleDecision(false)}
              disabled={deciding}
              variant="outline"
              className="flex-1 border-red-500/50 text-status-red hover:bg-status-red-dim/20"
              data-testid="reject-btn"
            >
              <ShieldX className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </div>
        </div>

        <p className="text-xs text-center text-txt-faint">CaseMinder — Bonsai Law Conflict Check Approval</p>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-0.5">{label}</p>
      <p className="text-sm text-txt-muted">{value || "-"}</p>
    </div>
  );
}
